import { useCallback } from 'react'
import { useLocation, useSearch } from 'wouter'

/**
 * Hook that provides search navigation functions.
 * This separates search-specific navigation from general navigation
 * to minimize rerenders in components that don't need search state.
 */
export const useSearchNavigation = () => {
  const [, setLocation] = useLocation()
  const search = useSearch()

  const updateSearchParams = useCallback((params: Record<string, string | null>) => {
    const currentParams = new URLSearchParams(search)
    
    // Update or remove parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === '') {
        currentParams.delete(key)
      } else {
        currentParams.set(key, value)
      }
    })
    
    const newSearch = currentParams.toString()
    setLocation(newSearch ? `/?${newSearch}` : '/')
  }, [search, setLocation])

  const setSearchQuery = useCallback((query: string) => {
    updateSearchParams({ q: query || null })
  }, [updateSearchParams])

  const setFilter = useCallback((type: string) => {
    updateSearchParams({ type: type || null })
  }, [updateSearchParams])

  return {
    updateSearchParams,
    setSearchQuery,
    setFilter,
  }
}